import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import moment from 'moment';
import { requestHandler } from './fetcher.saga';
import { getVariableServer } from '../selectors/global.selector';
import { addNotification } from '../actions/notification.action';
import { TAGS } from '../constants/notification';
import {
	PRODUCT_STOCK_GET,
	PRODUCT_STOCK_SUCCESS,
	PRODUCT_STOCK_ERROR,
	TYPE_MOVEMENTS_GET,
	TYPE_MOVEMENTS_SUCCESS,
	PRODUCT_MOVEMENT_CREATE,
	PRODUCT_MOVEMENT_CREATE_SUCCESS,
	PRODUCT_MOVEMENTS_GET,
	PRODUCT_MOVEMENTS_SUCCESS,
	PRODUCT_MOVEMENT_DELETE,
	PRODUCT_MOVEMENT_DELETE_SUCCESS,
	PRODUCT_STOCK_REMOVE,
	PRODUCT_STOCK_REMOVE_SUCCESS,
	PRODUCT_PURCHASE_PRICE_GET,
	PRODUCT_PURCHASE_PRICE_SUCCESS,
	PRODUCT_PURCHASE_PRICE_SET,
	PRODUCT_PURCHASE_PRICE_SET_SUCCESS,
	PRODUCT_PURCHASE_PRICE_DELETE,
	PRODUCT_PURCHASE_PRICE_DELETE_SUCCESS,
	PRODUCT_PURCHASE_PRICE_BULK_SET,
	PRODUCT_PURCHASE_PRICE_BULK_SET_SUCCESS,
	PRODUCT_PURCHASE_PRICE_BULK_DELETE,
} from '../constants/productStock';

function* fetchProductStock(action) {
	const result = yield call(
		requestHandler,
		'productStock',
		'GET',
		true,
		null,
		'',
		{ id_menu: action.id_menu }
	);
	if (result && result.result && result.result.result) {
		yield put({ type: PRODUCT_STOCK_SUCCESS, stocks: result.result.result });
	} else {
		yield put({ type: PRODUCT_STOCK_ERROR });
	}
}

function* fetchTypeMovements() {
	const result = yield call(
		requestHandler,
		'productStock',
		'GET',
		true,
		null,
		'/typeMovements',
		{}
	);
	if (result && result.result && result.result.result) {
		yield put({ type: TYPE_MOVEMENTS_SUCCESS, typeMovements: result.result.result });
	}
}

function* fetchMovements(action) {
	const result = yield call(
		requestHandler,
		'productStock',
		'GET',
		true,
		null,
		'/movements',
		{ id_product: action.id_product }
	);
	if (result && result.result && result.result.result) {
		yield put({ type: PRODUCT_MOVEMENTS_SUCCESS, movements: result.result.result });
	} else {
		yield put({ type: PRODUCT_MOVEMENTS_SUCCESS, movements: [] });
	}
}

function* postMovement(action) {
	if (action.data.quantityInfoData) {
		const qiResult = yield call(requestHandler, 'productStock', 'POST', true, action.data.quantityInfoData, '/quantityInfo', {});
		if (!qiResult || !qiResult.result) return;
	}
	const result = yield call(requestHandler, 'productStock', 'POST', true, action.data, '', {});
	if (result && result.result) {
		yield put({
			type: PRODUCT_MOVEMENT_CREATE_SUCCESS,
			id_global: action.data.id_product,
			delta: action.data.delta,
		});
		const isEntry = action.data.delta > 0;
		yield put(addNotification({
			tags: [TAGS.SUCCESS],
			text: `Stock — ${action.data.product_name} : ${isEntry ? 'Entrée' : 'Sortie'} de ${Math.abs(action.data.delta)} unité(s)`,
			date: moment().format('DD/MM/YYYY HH:mm'),
			viewed: false,
			openable: false,
		}));
		yield put({ type: PRODUCT_STOCK_GET, id_menu: action.data.id_menu });
	}
}

function* removeMovement(action) {
	const result = yield call(
		requestHandler,
		'productStock',
		'POST',
		true,
		{ id_movement: action.id_movement },
		'/deleteMovement',
		{}
	);
	if (result && result.result) {
		yield put({ type: PRODUCT_MOVEMENT_DELETE_SUCCESS, id_movement: action.id_movement });
		yield put({ type: PRODUCT_STOCK_GET, id_menu: action.id_menu });
		yield put({ type: PRODUCT_MOVEMENTS_GET, id_product: action.id_product });
	}
}

export function* watchProductStock() {
	yield takeLatest(PRODUCT_STOCK_GET, fetchProductStock);
}

export function* watchTypeMovements() {
	yield takeLatest(TYPE_MOVEMENTS_GET, fetchTypeMovements);
}

export function* watchCreateMovement() {
	while (true) {
		const action = yield take(PRODUCT_MOVEMENT_CREATE);
		yield call(postMovement, action);
	}
}

function* removeFromStock(action) {
	const result = yield call(
		requestHandler,
		'productStock',
		'POST',
		true,
		{ id_product: action.id_product },
		'/removeFromStock',
		{}
	);
	if (result && result.result) {
		yield put({ type: PRODUCT_STOCK_REMOVE_SUCCESS, id_product: action.id_product });
		yield put({ type: PRODUCT_STOCK_GET, id_menu: action.id_menu });
	}
}

export function* watchRemoveFromStock() {
	while (true) {
		const action = yield take(PRODUCT_STOCK_REMOVE);
		yield call(removeFromStock, action);
	}
}

export function* watchGetMovements() {
	yield takeLatest(PRODUCT_MOVEMENTS_GET, fetchMovements);
}

export function* watchDeleteMovement() {
	while (true) {
		const action = yield take(PRODUCT_MOVEMENT_DELETE);
		yield call(removeMovement, action);
	}
}

// "Prix d'achat HT" — deliberately independent of postMovement/
// fetchMovements above: those carry stock-list-refresh and "Entrée/Sortie
// de X unité(s)" notification side effects that don't apply here (this is
// a single quantity:0 price-only movement, not a real stock change).
function* fetchPurchasePrice(action) {
	const result = yield call(
		requestHandler,
		'productStock',
		'GET',
		true,
		null,
		'/purchasePrice',
		{ id_product: action.id_product }
	);
	const unit_value =
		result && result.result && result.result.result
			? result.result.result.unit_value
			: null;
	yield put({
		type: PRODUCT_PURCHASE_PRICE_SUCCESS,
		id_product: action.id_product,
		unit_value,
	});
}

// Jour de travail de l'app, au format 'YYYY-MM-DD' — la même date que celle
// affichée dans le calendrier et que le serveur reçoit via synchroParams.
function getSelectedDay(store) {
	const raw = getVariableServer(store.entity.variableServer, 'selected_date');
	return typeof raw === 'string' && raw.length >= 10
		? raw.slice(0, 10)
		: moment().format('YYYY-MM-DD');
}

function* postPurchasePrice(action) {
	const result = yield call(
		requestHandler,
		'productStock',
		'POST',
		true,
		{
			id_product: action.id_product,
			unit_value: action.unit_value,
			effective_day: action.effective_day,
		},
		'/purchasePrice',
		{}
	);
	if (result && result.result) {
		const store = yield select();
		yield put({
			type: PRODUCT_PURCHASE_PRICE_SET_SUCCESS,
			id_product: action.id_product,
			unit_value: action.unit_value,
			// Un prix daté après le jour de travail n'est pas encore en
			// vigueur : le serveur ne le renverrait pas, la mise à jour
			// optimiste de l'affichage ne doit donc pas l'appliquer.
			applyToDisplay:
				!action.effective_day ||
				action.effective_day <= getSelectedDay(store),
		});
	}
}

// Clearing the cell hard-deletes the quantity:0 price-only row rather than
// setting it to 0/empty — the server responds with whatever purchase price
// is now "latest" (a real stock movement's unit_value if one exists, else
// null), so the UI falls back to that instead of always going blank.
function* deletePurchasePrice(action) {
	// The server's generic /api/* route reads params from req.body for every
	// non-GET method (routes.js: `req.method === 'GET' ? req.query : req.body`)
	// — a DELETE with only url_params would arrive with an empty body and
	// fail checkParams(['id_product']) server-side.
	const result = yield call(
		requestHandler,
		'productStock',
		'DELETE',
		true,
		{ id_product: action.id_product },
		'/purchasePrice',
		{}
	);
	const unit_value =
		result && result.result && result.result.result
			? result.result.result.unit_value
			: null;
	if (result && result.result) {
		yield put({
			type: PRODUCT_PURCHASE_PRICE_DELETE_SUCCESS,
			id_product: action.id_product,
			unit_value,
		});
	}
}

export function* watchGetPurchasePrice() {
	yield takeLatest(PRODUCT_PURCHASE_PRICE_GET, fetchPurchasePrice);
}

export function* watchSetPurchasePrice() {
	while (true) {
		const action = yield take(PRODUCT_PURCHASE_PRICE_SET);
		yield call(postPurchasePrice, action);
	}
}

export function* watchDeletePurchasePrice() {
	while (true) {
		const action = yield take(PRODUCT_PURCHASE_PRICE_DELETE);
		yield call(deletePurchasePrice, action);
	}
}

// Bulk edit from the "Quantités" grid's multi-select toolbar — the client
// has already computed each item's final unit_value (fixed value, or the
// sale-price-HT discount) since it has the tax rates in memory; the server
// just upserts each row the same way postPurchasePrice does for one.
function* bulkSetPurchasePrice(action) {
	const result = yield call(
		requestHandler,
		'productStock',
		'POST',
		true,
		{ items: action.items },
		'/bulkPurchasePrice',
		{}
	);
	if (result && result.result) {
		yield put({
			type: PRODUCT_PURCHASE_PRICE_BULK_SET_SUCCESS,
			items: action.items,
		});
	}
}

export function* watchBulkSetPurchasePrice() {
	while (true) {
		const action = yield take(PRODUCT_PURCHASE_PRICE_BULK_SET);
		yield call(bulkSetPurchasePrice, action);
	}
}

// "Effacer le prix d'achat HT" from the multi-select toolbar — unlike the
// bulk set above, the resulting value per product isn't known client-side
// (it falls back to whatever real stock movement is now "latest", if any),
// so this reads the items back from the server's response rather than
// echoing what was sent. Reuses BULK_SET_SUCCESS since the shape (and the
// reducer update it needs to do) is identical either way.
function* bulkDeletePurchasePrice(action) {
	const result = yield call(
		requestHandler,
		'productStock',
		'DELETE',
		true,
		{ id_products: action.id_products },
		'/bulkPurchasePrice',
		{}
	);
	const items =
		result && result.result && result.result.result
			? result.result.result.items
			: null;
	if (items) {
		yield put({
			type: PRODUCT_PURCHASE_PRICE_BULK_SET_SUCCESS,
			items,
		});
	}
}

export function* watchBulkDeletePurchasePrice() {
	while (true) {
		const action = yield take(PRODUCT_PURCHASE_PRICE_BULK_DELETE);
		yield call(bulkDeletePurchasePrice, action);
	}
}
